import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/utils/api'
import { useSocketStore } from '@/stores/socket'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const token = ref(sessionStorage.getItem('korkem-auto-token') || localStorage.getItem('korkem-auto-token'))
  const loading = ref(false)
  const error = ref(null)
  const lastRoute = ref(localStorage.getItem('korkem-auto-lastRoute') || null)
  const verifyingToken = ref(false) // Add this to prevent double verification
  const initialized = ref(false) // Add this to track if auth is initialized

  // Computed
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userRole = computed(() => user.value?.role)
  const isStudent = computed(() => userRole.value === 'STUDENT')
  const isLimitedStudent = computed(() => userRole.value === 'LIMITED_STUDENT')
  const isInstructor = computed(() => userRole.value === 'INSTRUCTOR')
  const isTeacher = computed(() => userRole.value === 'THEORY_TEACHER')
  const isAdmin = computed(() => userRole.value === 'ADMIN')
  const hasTemporaryPassword = computed(() => user.value?.isTemporaryPassword === true)

  // Actions
  const login = async (credentials, rememberMe = false) => {
    loading.value = true
    error.value = null
    
    // Mobile debugging
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    // Console log removed
    // Console log removed
    
    try {
      const response = await api.post('/auth/login', credentials)
      
      // Console log removed
      
      token.value = response.data.token
      user.value = response.data.user
      
      // Mobile localStorage debugging
      try {
        if (rememberMe) {
          localStorage.setItem('korkem-auto-token', token.value)
          sessionStorage.removeItem('korkem-auto-token')
        } else {
          sessionStorage.setItem('korkem-auto-token', token.value)
          localStorage.removeItem('korkem-auto-token')
        }
        // Console log removed
      } catch (storageErr) {
        // Console log removed
        throw new Error('Мобилде деректерді сақтау мүмкін емес')
      }
      
      // Set authorization header for future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
      
      // Initialize socket connection
      initializeSocket()
      
      return response.data
    } catch (err) {
      // Console log removed
      
      if (err.code === 'ERR_NETWORK') {
        error.value = 'Серверге қосылу мүмкін емес. Желіні немесе серверді тексеріңіз.'
      } else {
        error.value = err.response?.data?.error || 'Кіру кезінде қате орын алды'
      }
      throw err
    } finally {
      loading.value = false
    }
  }

  const register = async (userData) => {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post('/auth/register', userData)
      return response.data
    } catch (err) {
      // Отображаем сообщение с бэка, а сетевые ошибки — отдельным текстом
      if (err.code === 'ERR_NETWORK') {
        error.value = 'Серверге қосылу мүмкін емес. Желіні немесе серверді тексеріңіз.'
      } else {
        error.value = err.response?.data?.error || 'Тіркелу кезінде қате орын алды'
      }
      throw err
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    const socketStore = useSocketStore()
    
    try {
      // Call backend logout to clean up session
      if (token.value) {
        await api.post('/auth/logout')
      }
    } catch (error) {
      // Console log removed
      // Continue with logout even if backend call fails
    }
    
    user.value = null
    token.value = null
    
    sessionStorage.removeItem('korkem-auto-token')
    localStorage.removeItem('korkem-auto-token')
    localStorage.removeItem('korkem-auto-lastRoute')
    delete api.defaults.headers.common['Authorization']
    
    // Disconnect socket
    socketStore.disconnect()
    
    // Clear last route on logout
    lastRoute.value = null
  }

  const forgotPassword = async (phone) => {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post('/auth/forgot-password', { phone })
      return response.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Құпия сөзді қалпына келтіру кезінде қате орын алды'
      throw err
    } finally {
      loading.value = false
    }
  }

  const changePassword = async (passwordData) => {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.post('/auth/change-password', passwordData)
      return response.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Құпия сөзді өзгерту кезінде қате орын алды'
      throw err
    } finally {
      loading.value = false
    }
  }

  const verifyToken = async () => {
    if (!token.value) return false
    
    // Prevent multiple simultaneous verification attempts
    if (verifyingToken.value) {
      // Wait for current verification to complete
      while (verifyingToken.value) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
      return !!user.value
    }
    
    verifyingToken.value = true
    loading.value = true
    
    try {
      // Set authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
      
      const response = await api.get('/auth/verify')
      user.value = response.data.user
      
      return true
    } catch (err) {
      // Better logging: include backend error code/message for easier debugging
      // Console log removed

      // Only logout if it's actually a 401/403 error, not network error
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        logout()
      }
      return false
    } finally {
      loading.value = false
      verifyingToken.value = false
    }
  }

  const updateProfile = async (profileData) => {
    loading.value = true
    error.value = null
    
    try {
      const response = await api.put('/users/profile', profileData)
      user.value = { ...user.value, ...response.data.user }
      return response.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Профильді жаңарту кезінде қате орын алды'
      throw err
    } finally {
      loading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  // Route management
  const setLastRoute = (route) => {
    lastRoute.value = route
    localStorage.setItem('korkem-auto-lastRoute', route)
  }

  const clearLastRoute = () => {
    lastRoute.value = null
    localStorage.removeItem('korkem-auto-lastRoute')
  }

  const getDefaultRouteForRole = (role) => {
    switch (role) {
      case 'ADMIN':
        return '/admin/dashboard'
      case 'ADMIN_LIMITED':
        return '/admin/dashboard' // ADMIN_LIMITED де админ панелін пайдаланады, бірақ шектеулі
      case 'INSTRUCTOR':
        return '/instructor/dashboard'
      case 'THEORY_TEACHER':
        return '/teacher/dashboard'
      case 'STUDENT':
        return '/'
      case 'LIMITED_STUDENT':
        return '/limited-student/dashboard'
      default:
        return '/'
    }
  }

  const getRedirectRoute = () => {
    // If there's a saved last route, use it
    if (lastRoute.value && lastRoute.value !== '/login' && lastRoute.value !== '/register') {
      return lastRoute.value
    }
    
    // Otherwise, use default route for user's role
    if (user.value) {
      return getDefaultRouteForRole(user.value.role)
    }
    
    return '/'
  }

  // Initialize socket connection for authenticated user
  // Note: App.vue handles socketStore.connect() on mount/auth change.
  // This only sets up the join room logic via socket 'connect' event.
  const initializeSocket = () => {
    if (user.value?.id) {
      const socketStore = useSocketStore()
      // Set userId so socket auto-joins on connect event
      socketStore.joinRoom(user.value.id)
    }
  }

  // Initialize auth state
  const initAuth = async () => {
    if (token.value) {
      const success = await verifyToken()
      if (success) {
        initializeSocket()
      }
      initialized.value = true
      return success
    }
    initialized.value = true
    return false
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    lastRoute,
    verifyingToken,
    initialized,
    
    // Computed
    isAuthenticated,
    userRole,
    isStudent,
    isLimitedStudent,
    isInstructor,
    isTeacher,
    isAdmin,
    hasTemporaryPassword,
    
    // Actions
    login,
    register,
    logout,
    forgotPassword,
    changePassword,
    verifyToken,
    updateProfile,
    clearError,
    initAuth,
    
    // Route management
    setLastRoute,
    clearLastRoute,
    getDefaultRouteForRole,
    getRedirectRoute
  }
})